/* eslint-disable @typescript-eslint/naming-convention */
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { makeApiCall, makeDownstreamApiCall } from "@cart-client/utils/Api";

// interfaces
import {
    NativeLocalCart,
    ReviewedCartApiResponse,
    AddressApiResponse,
    ICouponsAPIResponse,
} from "@swiggy-private/cart-types";
import { GetSingleOrderApiData } from "@cart-client/interfaces/Cart";
import { ApiResponse, RawApiResponse } from "@cart-client/interfaces/Api";

// constants
import { CouponPageType } from "@swiggy-private/checkout-core";
import { DOWNSTREAM_ENDPOINTS, DOWNSTREAM_HOSTS } from "@cart-client/constants/Hosts";
import {
    CAS_API_HEADERS,
    CAS_NEARBY_ADDRESS_API_PARAMS,
    NEAR_BY_ADDRESS_RADIUS_IN_MTR,
} from "@cart-client/constants/CartAddress";
import { addAdditionalHeadersToPLAPI } from "@swiggy-private/checkout-core";

// helpers
import { getUpdateCartParams } from "@cart-client/helpers/ApiHelper";

const ENDPOINT = {
    GET_CART: "/api/v1/get-cart",
    UPDATE_CART: "/api/v1/update-cart",
    APPLY_COUPON: "/api/v1/apply-coupon",
    REMOVE_COUPON: "/api/v1/remove-coupon",
    REMOVE_COUPON_PUBLIC: "/api/v3/remove-coupon",
    FETCH_COUPONS: "/api/v1/fetch-coupons",
    FETCH_NEARBY_ADDRESSES: "/addresses/nearby",
    GET_SINGLE_ORDER: "/api/v1/order/getSingleOrder",
    CBCC_REMIND_ME: "/v1/payments/cbcc/engagement/remindme",
};

export interface UpdateCartParams {
    [key: string]: unknown;
}

export interface CrossSellBodyItem {
    id: string;
}

export async function getCart(apiHost: string): Promise<ApiResponse<ReviewedCartApiResponse>> {
    const response = await makeApiCall<ReviewedCartApiResponse>({ apiHost, endpoint: ENDPOINT.GET_CART });
    return response;
}

export async function updateCart(
    apiHost: string,
    params: NativeLocalCart,
): Promise<ApiResponse<ReviewedCartApiResponse>> {
    const response = await makeApiCall<ReviewedCartApiResponse>({
        apiHost,
        endpoint: ENDPOINT.UPDATE_CART,
        // TODO: Revert after BE discussions regarding this epoch conversion from milli secs to secs.
        bodyParams: getUpdateCartParams(params),
        options: { method: "POST" },
    });
    return response;
}

export async function getSingleOrder(orderId: string): Promise<ApiResponse<GetSingleOrderApiData>> {
    const response = await makeDownstreamApiCall<ApiResponse<GetSingleOrderApiData>>({
        apiHost: DOWNSTREAM_HOSTS.CHECKOUT_READ_HOST,
        endpoint: ENDPOINT.GET_SINGLE_ORDER,
        options: {
            method: "GET",
        },
        searchParams: {
            order_id: orderId,
        },
    });
    return response;
}

export async function applyCoupon(apiHost: string, couponCode: string): Promise<ApiResponse<ReviewedCartApiResponse>> {
    const response = await makeApiCall<ReviewedCartApiResponse>({
        apiHost,
        endpoint: ENDPOINT.APPLY_COUPON,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        bodyParams: { coupon_code: couponCode },
        options: {
            method: "POST",
        },
    });
    return response;
}

export async function removeCouponPublic(
    apiHost: string,
    couponCode?: string,
): Promise<ApiResponse<ReviewedCartApiResponse>> {
    const response = await makeDownstreamApiCall<ApiResponse<ReviewedCartApiResponse>>({
        apiHost,
        endpoint: ENDPOINT.REMOVE_COUPON_PUBLIC,
        options: {
            method: "POST",
        },
        bodyParams: !isEmpty(couponCode) ? { coupon_code: couponCode } : undefined,
    });
    return response;
}

export async function removeCoupon(
    apiHost: string,
    couponCode?: string,
): Promise<ApiResponse<ReviewedCartApiResponse>> {
    const response = await makeApiCall<ReviewedCartApiResponse>({
        apiHost,
        endpoint: ENDPOINT.REMOVE_COUPON,
        options: {
            method: "POST",
        },
        bodyParams: !isEmpty(couponCode) ? { coupon_code: couponCode } : undefined,
    });
    return response;
}

export async function fetchCouponsPublic({
    lat,
    lng,
    cartValue,
    restaurantId,
    couponCode,
    subpage,
}: {
    lat: string;
    lng: string;
    cartValue: string;
    restaurantId: string;
    couponCode?: string;
    subpage?: CouponPageType;
}): Promise<ICouponsAPIResponse> {
    const response = await makeDownstreamApiCall<ICouponsAPIResponse>({
        apiHost: DOWNSTREAM_HOSTS.GANDALF_HOST,
        endpoint: DOWNSTREAM_ENDPOINTS.FETCH_COUPONS,
        searchParams: {
            lat: lat,
            lng: lng,
            source_page: "cart",
            restaurant_id: restaurantId,
            cart_value: cartValue,
            coupon_code: couponCode || "",
            sub_page: subpage || CouponPageType.RESTAURANT_COUPON_LISTING,
        },
        options: {
            headers: addAdditionalHeadersToPLAPI(),
        },
    });
    return response;
}

export async function fetchCouponsApi({
    apiHost,
    cartValue,
    restaurantId,
    couponCode,
}: {
    apiHost: string;
    cartValue: string;
    restaurantId: string;
    couponCode?: string;
}): Promise<ICouponsAPIResponse> {
    const response = await makeApiCall({
        apiHost,
        endpoint: ENDPOINT.FETCH_COUPONS,
        options: {
            method: "GET",
            headers: addAdditionalHeadersToPLAPI(),
        },
        searchParams: {
            cartValue,
            restaurantId,
            couponCode: couponCode || "",
        },
    });
    return response as ICouponsAPIResponse;
}

export async function recordCBCCInterest(): Promise<RawApiResponse> {
    const response = await makeApiCall({
        apiHost: DOWNSTREAM_HOSTS.CBCC_API_HOST,
        endpoint: ENDPOINT.CBCC_REMIND_ME,
        options: {
            method: "POST",
        },
    });
    return response;
}

export async function fetchNearByAddresses(
    lat: string | number,
    lng: string | number,
    userId: string,
): Promise<AddressApiResponse> {
    const response = await makeDownstreamApiCall<AddressApiResponse>({
        apiHost: DOWNSTREAM_HOSTS.CAS_HOST,
        endpoint: ENDPOINT.FETCH_NEARBY_ADDRESSES,
        options: {
            method: "GET",
            headers: {
                "client-id": CAS_API_HEADERS.CLIENT_ID,
                source: CAS_API_HEADERS.SOURCE,
            },
        },
        searchParams: {
            lat_long: `${lat},${lng}`,
            radius_mtr: `${NEAR_BY_ADDRESS_RADIUS_IN_MTR}`,
            entity_type: `${CAS_NEARBY_ADDRESS_API_PARAMS.ENTITY_TYPE}`,
            entity_id: userId,
            market_place_id: `${CAS_NEARBY_ADDRESS_API_PARAMS.MARKET_PLACE_ID}`,
            marketplace_business_lines: `${CAS_NEARBY_ADDRESS_API_PARAMS.MARKET_PLACE_BL}`,
        },
    });
    return response;
}
